import type { TMatter } from "../../models/matter-model";
import { StaffMemberPresentational } from "../StaffMember/StaffMemberPresentational";
import { MetricPayload, UNKNOWN_METRICS_VALUE } from "../../models/metric-model";
import { createAuthorisedApiRequest } from "../../helpers/create-authorised-request";
import { fetchWrapper } from "../../helpers/fetch-wrapper";
import { getDiagnostics } from "../../helpers/DiagnosticsHelper";
import { TSmokeballTier } from "../../services/smokeball-authenticator";
import ViewIcon from "../../../assets/svg/view.svg";
import AssignIcon from "../../../assets/svg/assign.svg";
import LeadSolidIcon from "../../../assets/svg/lead-solid.svg";
import MatterSolidIcon from "../../../assets/svg/matter-solid.svg";
import { getMatterLink } from "../../services/link-service";
import { SecondarySubtleButton } from "../Primitives/Buttons";
import { ViewType } from "../Search/SearchPresentational";

export type Props = {
  className?: string;
  matter: TMatter;
  isSelected?: boolean;
  allowAction?: boolean;
  fullAreaSelect?: boolean;
  searchViewType: ViewType;
  tier: TSmokeballTier;
  onMatterSelected?: (args: { matter: TMatter; isSelected: boolean; searchViewType: ViewType }) => void;
};

const onSelected = (props: Props) => {
  if (!props.allowAction) {
    return;
  }

  console.info(`Matter is selected: ${!props.isSelected}`);

  if (props.onMatterSelected) {
    console.info(`Invoking onMatterSelected. Matter ID: ${props.matter.matterId}. IsSelected: ${!props.isSelected}.`);

    props.onMatterSelected({
      matter: props.matter,
      isSelected: !props.isSelected,
      searchViewType: props.searchViewType,
    });
  }
};

const getMatterPresentational = (props: Props) => {
  async function onMatterOpened(): Promise<void> {
    const metricName = "outlook_addin_view";

    try {
      const diagnostics = getDiagnostics();

      let source: string;

      if (props.searchViewType === "RECENT") {
        source = "Recent matters";
      } else if (props.searchViewType === "SEARCH") {
        source = "Search";
      } else if (props.searchViewType === "DEFAULT") {
        source = "Assigned tile";
      } else {
        source = UNKNOWN_METRICS_VALUE;
      }

      const metricPayload: MetricPayload = {
        Metric: {
          Name: metricName,
          Timestamp: new Date().toISOString(), // Current UTC time
          Dimensions: {
            Platform: diagnostics?.platform || UNKNOWN_METRICS_VALUE,
            Version: diagnostics?.version || UNKNOWN_METRICS_VALUE,
            HostName: diagnostics?.hostName || UNKNOWN_METRICS_VALUE,
            HostVersion: diagnostics?.hostVersion || UNKNOWN_METRICS_VALUE,
            Source: source,
            Product: "",
          },
        },
      };

      const { url, requestInit } = await createAuthorisedApiRequest("AddMetricUrl", metricPayload);
      const result = await fetchWrapper(url, requestInit);

      if (result.err) {
        console.error(`Add metric call returned an error when saving ${metricName} metric: `, result.err);
      }
    } catch (error) {
      console.error(`Error occurred when saving ${metricName} metric: `, error);
    }
  }

  return (
    <div className={`${props.className || ""} flex`} id={props.matter.matterId}>
      {/* Tailwind won't work with w-1 or w-[4px]. Had to use inline style. */}
      {props.isSelected ? (
        <div className={props.matter.isLead ? "bg-black" : "bg-primary-orange-200"} style={{ width: "4px" }}></div>
      ) : null}

      <div className="flex flex-col flex-1 px-2 pt-2 pb-1 max-w-full">
        <div className="matter-icon-part flex flex-row">
          <img
            src={props.matter.isLead ? LeadSolidIcon : MatterSolidIcon}
            alt={props.matter.isLead ? "Lead" : "Matter"}
            className="self-start w-8 h-8 mt-1"
          />
          <div className="ml-2 overflow-hidden">
            <span className="font-semibold block overflow-hidden text-nowrap text-ellipsis">{props.matter.client}</span>
            <span className="block overflow-hidden text-ellipsis">{props.matter.matterTypeName}</span>
          </div>
        </div>
        <div className="matter-content-part flex flex-col mt-2 overflow-hidden">
          <span className="block overflow-hidden text-nowrap text-ellipsis" title={props.matter.description}>
            {props.matter.description}
          </span>
          <div className="flex flex-row gap-2 items-center justify-between overflow-hidden">
            <div className="flex items-center overflow-hidden text-nowrap mt-1">
              <span className="overflow-hidden text-nowrap text-ellipsis">
                Opened - {props.matter.dateOpened.toLocaleDateString()}
              </span>
            </div>
            <div className="staff-part ml-auto flex items-center text-nowrap overflow-hidden">
              {props.matter.personResponsible && (
                <StaffMemberPresentational staffMember={props.matter.personResponsible} initials={true} />
              )}
              {props.matter.personResponsible && props.matter.personAssisting ? <span className="w-1"></span> : null}
              {props.matter.personAssisting && (
                <StaffMemberPresentational staffMember={props.matter.personAssisting} initials={true} />
              )}
            </div>
          </div>
        </div>
        <div className="matter-action-part flex flex-row items-center justify-between border-t border-neutral-100 mt-3 pt-1">
          <div className="flex flex-1">
            <SecondarySubtleButton onClick={() => onSelected(props)} leftIcon={AssignIcon} className="flex-1">
              {props.isSelected ? "Unassign" : "Assign"}
            </SecondarySubtleButton>
            <SecondarySubtleButton
              onClick={async () => {
                await onMatterOpened();
                const matterUrl = getMatterLink(props.matter.matterId, props.tier);
                window.open(matterUrl, "_blank");
              }}
              target="_blank"
              leftIcon={ViewIcon}
              className="flex-1"
            >
              View
            </SecondarySubtleButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MatterPresentational = (props: Props) => {
  return getMatterPresentational(props);
};
